import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import {
  CompactTablePagination,
  EmptyState,
  ErrorState,
  LoadingState,
  SectionScaffold,
  SelectionBanner,
} from '@/shared/ui';

import {
  useAttributes,
  useBrowseProducts,
  useCreatePriceChangeBatch,
  useCustomerGroups,
  useDeleteProduct,
  useDryRunPriceChangeBatch,
  usePriceLists,
  useRetailStores,
  useSalesChannels,
  useSearchCategories,
  useStoreListBindings,
} from '../../hooks';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { attributeFilterLabel, describeAttributeFilter } from '../../lib/attributeFilterDisplay';
import { formatPrice, formatQuantity } from '../../lib/format';
import {
  COMPANY_SCOPE,
  decodeProductScope,
  encodeProductScope,
  productScopeWireValue,
  resolveActiveStoreListCode,
  resolveScopeDeclaredTarget,
  resolveUsedProvenance,
  showsProvenanceCaption,
} from '../../lib/productScope';
import type { ProductScope, UsedProvenance } from '../../lib/productScope';
import { useToast } from '../../lib/useToast';
import {
  COMMERCIAL_AVAILABILITY_LABELS,
  PRICE_STATUS_LABELS,
  PRODUCT_CARD_STATUS_LABELS,
  commercialAvailabilityColor,
  priceStatusColor,
  productCardStatusColor,
  STOCK_STATUS_LABELS,
  stockStatusColor,
} from '../../model/options';
import type { CreatePriceChangeBatchRequest } from '../../model/priceBatchTypes';
import type {
  BrowseProductsParams,
  Product,
  ProductAttributeSearchFilter,
  ProductSummary,
  StockStatus,
} from '../../model/types';
import { AttributeFiltersPopover } from '../components/AttributeFiltersPopover';
import { BulkStockUpdateDialog } from '../components/BulkStockUpdateDialog';
import { ImportFlyout } from '../components/ImportFlyout';
import type { PriceCellEditorContext } from '../components/PriceCellEditorDrawer';
import { PriceChangeBatchWizardDialog } from '../components/PriceChangeBatchWizardDialog';
import { ProductDetailDrawer } from '../components/ProductDetailDrawer';
import { QuickPriceDialog, QuickStockDialog } from '../components/ProductQuickEditDialogs';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Map a master browse summary onto the Product shape the quick-edit dialogs expect. */
function summaryToProduct(s: ProductSummary): Product {
  return {
    id: s.id,
    name: s.name,
    code: s.code,
    categoryId: s.categoryId ?? '',
    categoryName: s.categoryName ?? '',
    brand: s.brand,
    // Governed enrichment fields (plan-catalog-007): price/priceStatus from price-engine,
    // stockStatus from inventory availability. No oldPrice — the master copy is gone.
    price: s.price,
    priceStatus: s.priceStatus,
    imageUrl: s.imageUrl,
    stockStatus: (s.stockStatus ?? 'CHECK_AVAILABILITY') as StockStatus,
    // Pass status fields through so dialog context is accurate
    cardStatus: s.cardStatus,
    commercialAvailability: s.commercialAvailability,
  };
}

// ── Point-of-sale scope + provenance (design spec §8.10–§8.12, plan-merchant-portal-018 WP2.2/
// WP2.3) — module-level, pure-ish helpers (i18n `t` passed in) kept OUT of the component body so
// they read as one small, testable derivation, mirroring `PriceGridPanel.tsx`'s own `buildContext`. ─

/** MUI `Select` treats `''` as its own "nothing selected" state — a real "all customers" option
 * needs a distinct sentinel (mirrors `PriceGridPanel.tsx`'s own `ALL_CUSTOMERS_VALUE`, kept local
 * to each component rather than shared, the established precedent for this trivial a constant). */
const ALL_CUSTOMERS_VALUE = '__all-customers__';

interface ScopeNames {
  channel(code: string): string;
  list(code: string): string;
  group(code: string): string;
  store(code: string): string;
}

/**
 * `*Used` codes exist on the wire (possibly all `null`) once the BFF ships D3 — even a row that
 * resolved via the base slot still carries the three keys, just null-valued. Their outright
 * ABSENCE (the `in` check) is the only signal an older, pre-D3 BFF is being talked to (design spec
 * §8.11/§8.12: "D3 absent" degrades the caption to the honest "unknown scope" wording and the
 * quick-edit context to the scope-declared fallback) — `??`/`== null` alone cannot tell "explicitly
 * null" from "never sent" apart, so this checks key presence specifically.
 */
function hasProvenanceFields(product: ProductSummary): boolean {
  return (
    'priceChannelCodeUsed' in product ||
    'priceCustomerGroupCodeUsed' in product ||
    'priceListCodeUsed' in product
  );
}

/** The price column's provenance caption (design spec §8.11) — `undefined` when none should show
 * (company scope with no group, or the row didn't resolve at all — the status chip covers that). */
function priceColumnCaption(
  t: TFunction,
  product: ProductSummary,
  scope: ProductScope,
  groupCode: string | null,
  names: ScopeNames,
): string | undefined {
  if (product.priceStatus !== 'ACTIVE' || product.price == null) return undefined;
  if (!showsProvenanceCaption(scope, groupCode)) return undefined;
  if (!hasProvenanceFields(product)) {
    const scopeName =
      scope.kind === 'channel'
        ? names.channel(scope.code)
        : scope.kind === 'store'
          ? names.store(scope.code)
          : groupCode
            ? names.group(groupCode)
            : '';
    return t('products.provenance.unknown', { scope: scopeName });
  }
  const provenance = resolveUsedProvenance(product);
  const requestedChannelCode = scope.kind === 'channel' ? scope.code : null;
  switch (provenance.kind) {
    case 'list':
      return t('products.provenance.list', { name: names.list(provenance.code) });
    case 'channelGroup':
      return t('products.provenance.channelGroup');
    case 'group':
      return t('products.provenance.group', { name: names.group(provenance.code) });
    case 'channel':
      return provenance.code === requestedChannelCode
        ? t('products.provenance.channel', { name: names.channel(provenance.code) })
        : t('products.provenance.mirrored', { name: names.channel(provenance.code) });
    case 'base':
    default:
      return t('products.provenance.base');
  }
}

interface RowPriceEditTarget {
  context: PriceCellEditorContext;
  storeCaption?: string;
}

/**
 * The row quick-edit's editor context + store-scope caption (design spec §8.12, §8.4;
 * adr-merchant-portal-018 Decision 3 — "edit what you see"): provenance-driven when the row
 * resolved and D3 is live, the scope-declared slot as the fallback otherwise. `activeStoreListCode`
 * is the store scope's OWN bound-active-list lookup (resolved by the caller — this function stays
 * synchronous/pure).
 */
function resolveRowPriceEditTarget(
  t: TFunction,
  product: ProductSummary,
  scope: ProductScope,
  groupCode: string | null,
  names: ScopeNames,
  activeStoreListCode: string | null,
): RowPriceEditTarget {
  const isStoreScope = scope.kind === 'store';
  const storeDisplayName = isStoreScope && scope.kind === 'store' ? names.store(scope.code) : '';
  const groupContext = groupCode ? { code: groupCode, name: names.group(groupCode) } : undefined;

  if (product.priceStatus === 'ACTIVE' && hasProvenanceFields(product)) {
    const provenance: UsedProvenance = resolveUsedProvenance(product);
    switch (provenance.kind) {
      case 'list': {
        const name = names.list(provenance.code);
        return {
          context: { type: 'list', code: provenance.code, name },
          storeCaption: isStoreScope
            ? t('priceCellEditorDrawer.scope.fromStoreList', { store: storeDisplayName, list: name })
            : undefined,
        };
      }
      case 'channelGroup':
        return {
          context: {
            type: 'channel',
            code: provenance.channelCode,
            name: names.channel(provenance.channelCode),
            group: { code: provenance.groupCode, name: names.group(provenance.groupCode) },
          },
          storeCaption: isStoreScope
            ? t('priceCellEditorDrawer.scope.fromStoreChannel', {
                store: storeDisplayName,
                channel: names.channel(provenance.channelCode),
              })
            : undefined,
        };
      case 'channel': {
        const name = names.channel(provenance.code);
        return {
          context: { type: 'channel', code: provenance.code, name },
          storeCaption: isStoreScope
            ? t('priceCellEditorDrawer.scope.fromStoreChannel', { store: storeDisplayName, channel: name })
            : undefined,
        };
      }
      case 'group':
        return {
          context: { type: 'base', group: { code: provenance.code, name: names.group(provenance.code) } },
          storeCaption: isStoreScope
            ? t('priceCellEditorDrawer.scope.fromStoreBase', { store: storeDisplayName })
            : undefined,
        };
      case 'base':
      default:
        return {
          context: { type: 'base' },
          storeCaption: isStoreScope
            ? t('priceCellEditorDrawer.scope.fromStoreBase', { store: storeDisplayName })
            : undefined,
        };
    }
  }

  // Fallback — nothing resolved (NO_PRICE etc.), or an older BFF hasn't shipped D3 yet
  // (adr-merchant-portal-018 Decision 3 + reconciliation note 1: the scope-declared slot, never a
  // client ladder walk — the list holds no per-row price lines to walk).
  const fallback = resolveScopeDeclaredTarget(scope, activeStoreListCode);
  switch (fallback.kind) {
    case 'channel':
      return {
        context: { type: 'channel', code: fallback.code, name: names.channel(fallback.code), group: groupContext },
      };
    case 'list': {
      const name = names.list(fallback.code);
      return {
        context: { type: 'list', code: fallback.code, name },
        storeCaption: t('priceCellEditorDrawer.scope.fromStoreList', { store: storeDisplayName, list: name }),
      };
    }
    case 'unboundStoreBase':
      return {
        context: { type: 'base', group: groupContext },
        storeCaption: t('priceCellEditorDrawer.storeUnbound', { code: fallback.storeCode }),
      };
    case 'base':
    default:
      return { context: { type: 'base', group: groupContext } };
  }
}

interface BulkTargetSeed {
  initialTargetChannelCode?: string;
  initialTargetGroupCode?: string;
  initialTargetListCode?: string;
  initialTargetLabel: string;
  targetInfoNote?: string;
}

/**
 * The bulk-repricing wizard's `initialTarget*` seed (design spec §8.13) — derived from the
 * CURRENT scope: a point-of-sale scope (channel/store) takes precedence over the customer-group
 * axis, because `CreatePriceChangeBatchRequest.target*` is a single dimension (mutually exclusive
 * on the wire) and the wizard's own Step 3 stays fully editable regardless — this is a *prefill*
 * convenience, not a constraint. Reuses the workspace's own `bulkBatchesPanel.target.listChip`/
 * `.groupChip`/`.base` labels (`PriceChangeBatchesPanel.tsx`'s `targetChip()` precedent) so the
 * label vocabulary stays in one place.
 */
function resolveBulkTargetSeed(
  t: TFunction,
  scope: ProductScope,
  groupCode: string | null,
  activeStoreListCode: string | null,
  names: ScopeNames,
): BulkTargetSeed {
  if (scope.kind === 'channel') {
    return { initialTargetChannelCode: scope.code, initialTargetLabel: names.channel(scope.code) };
  }
  if (scope.kind === 'store') {
    if (activeStoreListCode) {
      return {
        initialTargetListCode: activeStoreListCode,
        initialTargetLabel: t('bulkBatchesPanel.target.listChip', { name: names.list(activeStoreListCode) }),
      };
    }
    return {
      initialTargetLabel: t('bulkBatchesPanel.target.base'),
      targetInfoNote: t('products.bulkReprice.storeUnbound', { code: scope.code }),
    };
  }
  if (groupCode) {
    return {
      initialTargetGroupCode: groupCode,
      initialTargetLabel: t('bulkBatchesPanel.target.groupChip', { name: names.group(groupCode) }),
    };
  }
  return { initialTargetLabel: t('bulkBatchesPanel.target.base') };
}

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * Merchant product catalogue — browses the FULL master catalog (drafts included)
 * with search, status/category filters, inline price & stock edits, and delete
 * (epic-catalog-002, story-024).
 *
 * This page is the **reference implementation** for table-format pages in the
 * merchant portal, including its i18n wiring (plan-009 Phase 1 reference). Key
 * shared patterns used here:
 *
 * - `SectionScaffold` — header + search + settings gear
 * - `SelectionBanner` — bulk-selection state bar
 * - `CompactTablePagination` — compact footer pagination with rows-per-page
 * - `stickyHeader` + `maxHeight` on `TableContainer` for scrollable tables
 * - Responsive column visibility (`display: { xs: 'none', sm: 'table-cell' }`)
 * - `useTranslation('catalog')` for page copy; `t('common:...')` for anything
 *   already covered by the shared `common.json` namespace (Save/Cancel/Delete
 *   etc.); enum→label dictionaries from `model/options.ts` hold i18next KEYS,
 *   rendered via `t(...)` at the point of use.
 *
 * **Attribute filters wiring (user-story-merchant-portal-033).** This page already fetched via
 * `useBrowseProducts` → `POST /catalog/products/browse` → `ProductSearchFilter` (the FULL master
 * catalog, `web-catalog-master-service`) — and that is exactly the schema epic-catalog-005 added
 * `attributes: ProductAttributeSearchFilter[]` to, not `POST /products/search` (the OpenSearch
 * storefront-mirroring search this page does NOT use). So no endpoint switch was needed: `browse`
 * already IS the path that reaches the master's merchant-facing filtering, and the new
 * `attributeFilters` state slots straight into the existing `browseParams`. The bulk "select all
 * pages" action still goes through `POST /products/search` (`ProductSearchRequest`) for its
 * *count*, which has no attribute-filter field — so, exactly like the pre-existing status-filter
 * restriction, "select all" is disabled whenever an attribute filter is active (see
 * `toggleSelectAll`).
 */
export function ProductsPage() {
  const { t, i18n } = useTranslation('catalog');
  const navigate = useNavigate();
  const { canView, canManage, canPricingView, canPricingManage, canPricingBulk } = useCatalogPermissions();
  const { hasPermission } = usePermissions();
  // The retail-store directory needs `inventory.view` (a DIFFERENT feature's permission namespace,
  // spec §4/Q7) — the same raw-string gating precedent `ProductPricesDrawer`'s own check section
  // already uses for this exact permission.
  const hasInventoryView = hasPermission('inventory.view');
  const { toast, success, error: notifyError, clear } = useToast();

  const STATUS_FILTER_OPTIONS = useMemo(
    () => [
      { value: 'all', label: t('products.statusFilter.all') },
      { value: 'DRAFT', label: t('products.statusFilter.draft') },
      { value: 'PUBLISHED', label: t('products.statusFilter.published') },
      { value: 'ARCHIVED', label: t('products.statusFilter.archived') },
    ],
    [t],
  );

  const BROWSE_SORT_OPTIONS = useMemo(
    () => [
      { value: 'updatedAt,desc', label: t('products.sort.updatedDesc') },
      { value: 'name,asc', label: t('products.sort.nameAsc') },
      { value: 'name,desc', label: t('products.sort.nameDesc') },
    ],
    [t],
  );

  // ── Filter & pagination state ──────────────────────────────────────────────
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('updatedAt,desc');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  // Attribute-value filters (user-story-merchant-portal-033) — wired through the master-catalog
  // browse endpoint (see the module doc below for why /browse, not /search).
  const [attributeFilters, setAttributeFilters] = useState<ProductAttributeSearchFilter[]>([]);

  // ── Point-of-sale scope + customer group (design spec §8.10, plan-merchant-portal-018 WP2.2) ──
  // `?scope=`/`?group=` round-trip through the URL, mirroring `PricingPage`'s own `?scope=`
  // lifted-state convention (`replace`, read on mount, written on change) — every OTHER filter on
  // this page stays local-only state, unchanged.
  const [searchParams, setSearchParams] = useSearchParams();
  const [scope, setScope] = useState<ProductScope>(() => decodeProductScope(searchParams.get('scope')));
  const [groupCode, setGroupCode] = useState<string>(() => searchParams.get('group') ?? ALL_CUSTOMERS_VALUE);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const encodedScope = encodeProductScope(scope);
    if (encodedScope === COMPANY_SCOPE) next.delete('scope');
    else next.set('scope', encodedScope);
    if (groupCode !== ALL_CUSTOMERS_VALUE) next.set('group', groupCode);
    else next.delete('group');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, groupCode]);

  /** Changing the scope resets the page but KEEPS the selection (design spec §8.10) — the
   * selection is a set of ids, scope-independent. */
  const handleScopeChange = (value: string) => {
    setScope(decodeProductScope(value));
    setPage(0);
  };

  // ── Dialog / drawer targets ────────────────────────────────────────────────
  const [drawerProduct, setDrawerProduct] = useState<ProductSummary | null>(null);
  // The full summary, not a mapped-down `Product` (WP2.3) — the row quick-edit's editor context
  // is derived from the summary's own `*Used` provenance ("edit what you see").
  const [priceTarget, setPriceTarget] = useState<ProductSummary | null>(null);
  const [stockTarget, setStockTarget] = useState<Product | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductSummary | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkRepriceOpen, setBulkRepriceOpen] = useState(false);

  // ── Bulk-selection state ───────────────────────────────────────────────────
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  /**
   * Selection persists across pagination (only cleared on a filter/sort change or an explicit
   * "clear"), so a merchant can check rows on page 1, page forward and check more — but the bulk
   * repricing wizard needs each selected product's NAME to render as an editable chip (design spec
   * §8.13), and a product picked on an earlier page is no longer present in the current page's
   * `products` array. This ref accumulates every `ProductSummary` ever selected (imperative, not
   * reactive state — it's read once, at wizard-open time) so the wizard's prefill never silently
   * drops a cross-page selection, which would otherwise reprice fewer products than the merchant
   * saw selected (a data-integrity gap, not just a display one).
   */
  const selectedProductsRef = useRef<Map<string, ProductSummary>>(new Map());

  // ── Debounce free-text search ──────────────────────────────────────────────
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQuery(queryInput.trim());
      setPage(0);
    }, 350);
    return () => window.clearTimeout(handle);
  }, [queryInput]);

  // ── Data ───────────────────────────────────────────────────────────────────
  const categoriesQuery = useSearchCategories(
    { page: 0, size: 100, sort: 'name,asc' },
    { enabled: canView },
  );
  const attributesQuery = useAttributes({ enabled: canView });
  const deleteMutation = useDeleteProduct();

  // ── Point-of-sale scope registries (design spec §8.10) — each gated by its OWN permission;
  // a missing/failed source drops its own option group silently (never blocks the page). ────────
  const channelsQuery = useSalesChannels({ enabled: canPricingView });
  const listsQuery = usePriceLists({ enabled: canPricingView });
  const groupsQuery = useCustomerGroups({ enabled: canPricingView });
  const storesQuery = useRetailStores({ enabled: hasInventoryView });
  // Store→bound-active-list lookup — only needed once a store scope is actually selected (the
  // fallback target derivation and the price column's "store" caption path).
  const { bindingsByCode: storeBindingsByCode } = useStoreListBindings({
    enabled: canPricingView && scope.kind === 'store',
  });

  const allChannels = channelsQuery.data ?? [];
  const activeChannels = allChannels.filter((c) => c.active);
  const allLists = listsQuery.data ?? [];
  const allGroups = groupsQuery.data ?? [];
  const activeGroups = allGroups.filter((g) => g.active);
  const allStores = hasInventoryView && !storesQuery.isError ? (storesQuery.data ?? []) : [];

  // §8.10: "if no group [of options] is available the whole selector is hidden" — channels need
  // `catalog.pricing.view`, stores need `inventory.view`, independently of each other.
  const showScopeSelector = activeChannels.length > 0 || allStores.length > 0;
  // The group axis is gated on ACTIVE groups specifically (deliberately different from the grid's
  // OWN "any group at all" gate — spec §8.10's literal wording for THIS selector).
  const showGroupSelector = canPricingView && activeGroups.length > 0;

  const scopeNames: ScopeNames = {
    channel: (code) => allChannels.find((c) => c.code === code)?.name ?? code,
    list: (code) => allLists.find((l) => l.code === code)?.name ?? code,
    group: (code) => allGroups.find((g) => g.code === code)?.name ?? code,
    store: (code) => allStores.find((s) => s.code === code)?.name ?? code,
  };

  const activeStoreListCode =
    scope.kind === 'store' ? resolveActiveStoreListCode(scope.code, allLists, storeBindingsByCode) : null;

  const effectiveGroupCode = groupCode !== ALL_CUSTOMERS_VALUE ? groupCode : null;

  const browseParams = useMemo<BrowseProductsParams>(
    () => ({
      query: query || undefined,
      status: status === 'all' ? undefined : (status as 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'),
      categoryId: categoryId || undefined,
      attributes: attributeFilters.length > 0 ? attributeFilters : undefined,
      // One point-of-sale selection drives BOTH enrichments (design spec §8.10, D1); the
      // customer-group axis is price-only (D2) — stock has no customer-group concept.
      stockScope: productScopeWireValue(scope),
      priceScope: productScopeWireValue(scope),
      priceCustomerGroupCode: effectiveGroupCode ?? undefined,
      page,
      size,
      sort,
    }),
    [query, status, categoryId, attributeFilters, scope, effectiveGroupCode, sort, page, size],
  );

  const productsQuery = useBrowseProducts(browseParams, { enabled: canView });
  const products = productsQuery.data?.content ?? [];
  const total = productsQuery.data?.totalElements ?? 0;

  // plan-catalog-007: filter-based bulk stock ("select all matching") is gone — inventory
  // adjustments name every product explicitly, so the bulk dialog takes only the explicit ids.

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Reset to page 0 when any filter changes. */
  const resetPageAnd = (fn: () => void) => {
    fn();
    setPage(0);
  };

  // Clear selection whenever filters or sort change — scope/group are deliberately EXCLUDED
  // (design spec §8.10: changing the scope keeps the selection).
  useEffect(() => {
    setSelectedProductIds(new Set());
    setSelectAllMode(false);
    selectedProductsRef.current.clear();
  }, [query, categoryId, status, attributeFilters, sort]);

  // ── Selection ──────────────────────────────────────────────────────────────

  const toggleProductSelection = (product: ProductSummary) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(product.id)) {
        next.delete(product.id);
        if (selectAllMode && next.size === 0) setSelectAllMode(false);
      } else {
        next.add(product.id);
        selectedProductsRef.current.set(product.id, product);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectAllMode) {
      setSelectAllMode(false);
      setSelectedProductIds(new Set());
    } else if (status === 'all' && attributeFilters.length === 0) {
      // "Select all pages" remains a UI selection concept (delete etc.); the bulk STOCK dialog
      // no longer supports it (plan-catalog-007: inventory adjustments name every product
      // explicitly) and explains that itself.
      setSelectAllMode(true);
      setSelectedProductIds(new Set(products.map((p) => p.id)));
      products.forEach((p) => selectedProductsRef.current.set(p.id, p));
    } else {
      // A status or attribute filter is active → select current page by explicit IDs only.
      setSelectedProductIds(new Set(products.map((p) => p.id)));
      products.forEach((p) => selectedProductsRef.current.set(p.id, p));
    }
  };

  const clearSelection = () => {
    setSelectedProductIds(new Set());
    setSelectAllMode(false);
    selectedProductsRef.current.clear();
  };

  const isAllCurrentPageSelected =
    products.length > 0 && products.every((p) => selectAllMode || selectedProductIds.has(p.id));

  const selectedCount = selectAllMode ? total : selectedProductIds.size;

  // ── Delete ─────────────────────────────────────────────────────────────────

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const name = deleteTarget.name;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        success(t('products.delete.success', { name }));
        setDeleteTarget(null);
      },
      onError: (e) => {
        notifyError(e.message ?? t('products.delete.error'));
        setDeleteTarget(null);
      },
    });
  };

  // ── Bulk entry "Reprice selected" (design spec §8.13, plan-merchant-portal-018 WP2.4) ─────────

  // Row price action gate (design spec §4/§8.12 — the same class of fix as R5 on the card): the
  // action used to be gated on `catalog.manage`; now it's the PRICING codes specifically, with a
  // read-only middle tier for a viewer.
  const priceActionMode: 'manage' | 'view' | 'none' = canPricingManage ? 'manage' : canPricingView ? 'view' : 'none';
  const priceActionTooltip =
    priceActionMode === 'manage'
      ? t('products.rowActions.editPrice')
      : priceActionMode === 'view'
        ? t('products.rowActions.viewPrice')
        : t('products.rowActions.editPriceNoAccess');

  const canBulkReprice = canPricingManage && canPricingBulk;
  const bulkRepriceDisabled = !canBulkReprice || selectAllMode;
  const bulkRepriceTooltip = selectAllMode
    ? t('products.bulkReprice.selectAllUnsupported')
    : !canBulkReprice
      ? t('bulkBatchesPanel.noBulkPermission')
      : t('products.bulkReprice.tooltip');

  // Cross-page-safe (see `selectedProductsRef`'s own doc comment above).
  const bulkInitialProducts = useMemo(
    () =>
      [...selectedProductIds]
        .map((id) => selectedProductsRef.current.get(id))
        .filter((p): p is ProductSummary => Boolean(p))
        .map(summaryToProduct),
    [selectedProductIds],
  );

  const bulkTargetSeed = resolveBulkTargetSeed(t, scope, effectiveGroupCode, activeStoreListCode, scopeNames);

  // The quick-edit's scoped editor context (design spec §8.12) — computed once per render from
  // whichever row currently owns `priceTarget` (the row icon, or the detail drawer's "Price"
  // footer button both funnel through this same state).
  const priceEditTarget = priceTarget
    ? resolveRowPriceEditTarget(t, priceTarget, scope, effectiveGroupCode, scopeNames, activeStoreListCode)
    : null;

  const createBatchMutation = useCreatePriceChangeBatch();
  const dryRunBatchMutation = useDryRunPriceChangeBatch();

  const handleCreateRepriceBatch = (body: CreatePriceChangeBatchRequest) => {
    createBatchMutation.mutate(body, {
      onSuccess: (created) => {
        dryRunBatchMutation.mutate(created.id, {
          onSuccess: () => {
            setBulkRepriceOpen(false);
            clearSelection();
            success(t('bulkBatchesPanel.created'));
            navigate(`/catalog/pricing?tab=bulk&view=batches&batch=${created.id}`);
          },
          onError: (error) => {
            // The batch itself was created — show its detail (via the workspace) so the
            // merchant can retry the dry-run there, mirroring `PriceChangeBatchesPanel`'s own
            // create-then-dry-run error handling.
            setBulkRepriceOpen(false);
            clearSelection();
            notifyError(error.message);
            navigate(`/catalog/pricing?tab=bulk&view=batches&batch=${created.id}`);
          },
        });
      },
      onError: (error) => notifyError(error.message),
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <SectionScaffold
      title={t('products.title')}
      subtitle={t('products.subtitle')}
      canView={canView}
      settingsTo="/settings/catalog"
      settingsLabel={t('products.settingsLabel')}
      search={{
        value: queryInput,
        onChange: setQueryInput,
        placeholder: t('products.searchPlaceholder'),
      }}
      primaryAction={
        canManage || selectedCount > 0 ? (
          <>
            {selectedCount > 0 && canManage && (
              <Tooltip
                title={
                  selectAllMode
                    ? t('products.bulkStock.tooltipAll')
                    : t('products.bulkStock.tooltipSelected')
                }
              >
                <Button
                  variant="outlined"
                  startIcon={<WarehouseRoundedIcon />}
                  onClick={() => setBulkDialogOpen(true)}
                >
                  {t('products.bulkStock.button', { count: selectedCount })}
                </Button>
              </Tooltip>
            )}
            {/* "Reprice selected" (design spec §8.13) — independent of `catalog.manage` (a
                pricing-only manager can bulk-reprice without editing products otherwise). */}
            {selectedCount > 0 && (
              <Tooltip title={bulkRepriceTooltip}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<SellRoundedIcon />}
                    disabled={bulkRepriceDisabled}
                    onClick={() => setBulkRepriceOpen(true)}
                  >
                    {t('products.bulkReprice.button', { count: selectedCount })}
                  </Button>
                </span>
              </Tooltip>
            )}
            {canManage && (
              <Button
                variant="outlined"
                startIcon={<UploadFileRoundedIcon />}
                onClick={() => setImportOpen(true)}
              >
                {t('common:action.import')}
              </Button>
            )}
            {canManage && (
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => navigate('/catalog/products/new')}
              >
                {t('products.actions.add')}
              </Button>
            )}
          </>
        ) : undefined
      }
    >
      <Stack spacing={2.5}>

        {/* ── Filter bar ────────────────────────────────────────────────── */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label={t('products.statusFilterLabel')}
            value={status}
            onChange={(e) => resetPageAnd(() => setStatus(e.target.value))}
            sx={{ minWidth: 180 }}
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('products.categoryFilterLabel')}
            value={categoryId}
            onChange={(e) => resetPageAnd(() => setCategoryId(e.target.value))}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('products.allCategories')}</MenuItem>
            {(categoriesQuery.data?.content ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('products.sortLabel')}
            value={sort}
            onChange={(e) => resetPageAnd(() => setSort(e.target.value))}
            sx={{ minWidth: 200 }}
          >
            {BROWSE_SORT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <AttributeFiltersPopover
            dictionary={attributesQuery.data ?? []}
            active={attributeFilters}
            onChange={(next) => resetPageAnd(() => setAttributeFilters(next))}
          />

          {/* Point-of-sale scope + customer group (design spec §8.10) — a single point-of-sale
              selector drives BOTH the price and stock enrichment columns; the group axis is
              price-only and composes with every scope. Hidden entirely when no option group
              (channels/lists via `catalog.pricing.view`, stores via `inventory.view`) is
              readable — the page then behaves exactly as it did before this feature. */}
          {showScopeSelector || showGroupSelector ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ ml: { sm: 'auto' }, alignItems: { sm: 'flex-start' } }}
            >
              {showScopeSelector ? (
                <Stack spacing={0.25}>
                  <TextField
                    select
                    size="small"
                    label={t('products.scope.label')}
                    value={encodeProductScope(scope)}
                    onChange={(e) => handleScopeChange(e.target.value)}
                    sx={{ minWidth: 240 }}
                    slotProps={{ select: { 'aria-label': t('products.scope.aria') } }}
                  >
                    <MenuItem value={COMPANY_SCOPE}>{t('products.scope.company')}</MenuItem>
                    {activeChannels.length > 0 ? (
                      <ListSubheader>{t('products.scope.groups.channels')}</ListSubheader>
                    ) : null}
                    {activeChannels.map((c) => (
                      <MenuItem key={c.code} value={encodeProductScope({ kind: 'channel', code: c.code })}>
                        {c.name}
                      </MenuItem>
                    ))}
                    {allStores.length > 0 ? (
                      <ListSubheader>{t('products.scope.groups.stores')}</ListSubheader>
                    ) : null}
                    {allStores.map((s) => (
                      <MenuItem
                        key={s.code}
                        value={encodeProductScope({ kind: 'store', code: s.code })}
                        disabled={s.active === false}
                      >
                        {s.name}{' '}
                        <Box component="span" sx={{ fontFamily: 'JetBrains Mono, monospace', ml: 0.5 }}>
                          {s.code}
                        </Box>
                        {s.active === false ? ` (${t('products.scope.groups.inactiveSuffix')})` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: 'none', lg: 'block' } }}
                  >
                    {scope.kind === 'company'
                      ? t('products.scope.helper.company')
                      : scope.kind === 'channel'
                        ? t('products.scope.helper.channel')
                        : t('products.scope.helper.store')}
                  </Typography>
                </Stack>
              ) : null}
              {showGroupSelector ? (
                <TextField
                  select
                  size="small"
                  label={t('products.group.label')}
                  value={groupCode}
                  onChange={(e) => setGroupCode(e.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value={ALL_CUSTOMERS_VALUE}>{t('products.group.all')}</MenuItem>
                  {activeGroups.map((g) => (
                    <MenuItem key={g.code} value={g.code}>
                      {g.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
            </Stack>
          ) : null}
        </Stack>

        {/* Active attribute filters — deletable chips (user-story-merchant-portal-033) */}
        {attributeFilters.length > 0 ? (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {attributeFilters.map((filter) => (
              <Chip
                key={filter.code}
                size="small"
                variant="outlined"
                color="primary"
                label={`${attributeFilterLabel(filter.code, attributesQuery.data ?? [], i18n.language)}: ${describeAttributeFilter(filter, attributesQuery.data ?? [], i18n.language, t)}`}
                onDelete={() =>
                  resetPageAnd(() => setAttributeFilters((prev) => prev.filter((f) => f.code !== filter.code)))
                }
              />
            ))}
          </Stack>
        ) : null}

        {/* ── Table area ────────────────────────────────────────────────── */}
        <Box>
          {/*
           * SelectionBanner — shared component for bulk-action workflows.
           * Appears above the table when ≥1 rows are checked.
           */}
          {selectedCount > 0 && (
            <SelectionBanner
              label={
                selectAllMode
                  ? t('products.selection.allSelected', { total })
                  : t('products.selection.count', { count: selectedCount })
              }
              onClear={clearSelection}
            />
          )}

          {/*
           * Refetch-in-place (design spec §9's closing line) — a reserved 4px slot above the
           * table so a background refetch (e.g. a scope change) never shifts the layout. Only
           * shown for a background refetch, never the first load (`LoadingState` below already
           * covers that) — same idiom as `StoresPanel.tsx`'s own scope-refetch indicator.
           */}
          {productsQuery.isFetching && !productsQuery.isLoading ? (
            <LinearProgress sx={{ height: 4 }} />
          ) : (
            <span style={{ height: 4, display: 'block' }} />
          )}

          {/* Content states */}
          {productsQuery.isLoading ? (
            <LoadingState label={t('products.loading')} />
          ) : productsQuery.isError ? (
            <ErrorState
              title={t('products.loadError.title')}
              message={productsQuery.error?.message ?? t('common:error.unknown')}
              onRetry={() => void productsQuery.refetch()}
            />
          ) : products.length === 0 ? (
            <EmptyState
              title={
                query || categoryId || status !== 'all'
                  ? t('common:empty.noResults')
                  : t('products.empty.title')
              }
              description={
                query || categoryId || status !== 'all'
                  ? t('products.empty.filteredDescription')
                  : t('products.empty.description')
              }
              icon={<Inventory2RoundedIcon />}
              action={
                canManage ? (
                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={() => navigate('/catalog/products/new')}
                  >
                    {t('products.actions.add')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              {/*
               * TableContainer with maxHeight + stickyHeader keeps the column
               * labels visible while scrolling a large result set. The pagination
               * sits inline below — always visible, no pinned footer needed.
               */}
              <TableContainer sx={{ maxHeight: { xs: 480, sm: 560, md: 640 } }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={!isAllCurrentPageSelected && selectedCount > 0}
                          checked={isAllCurrentPageSelected || selectAllMode}
                          onChange={toggleSelectAll}
                        />
                      </TableCell>
                      <TableCell>{t('products.table.product')}</TableCell>
                      {/* Hidden on xs — screen too narrow for 6 columns */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {t('products.table.category')}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {/* Sticky header keeps the scope visible while scrolling (design spec
                            §8.11) — the header suffix truncates to the raw code below `lg`. */}
                        {scope.kind === 'company'
                          ? t('products.table.price')
                          : t('products.table.priceScoped', {
                              scope:
                                scope.kind === 'channel'
                                  ? scopeNames.channel(scope.code)
                                  : scopeNames.store(scope.code),
                            })}
                      </TableCell>
                      <TableCell>{t('products.table.status')}</TableCell>
                      <TableCell align="right">{t('products.table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow
                        key={product.id}
                        hover
                        selected={selectAllMode || selectedProductIds.has(product.id)}
                        onClick={() => setDrawerProduct(product)}
                        sx={{ cursor: 'pointer' }}
                      >
                        {/*
                         * stopPropagation on checkbox cell prevents the row-click drawer
                         * from firing when the merchant is selecting rows for bulk actions.
                         */}
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ cursor: 'default' }}
                        >
                          <Checkbox
                            checked={selectAllMode || selectedProductIds.has(product.id)}
                            onChange={() => toggleProductSelection(product)}
                          />
                        </TableCell>

                        {/* Product name + code/brand */}
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              variant="rounded"
                              src={product.imageUrl}
                              sx={{ width: 40, height: 40, flexShrink: 0, bgcolor: 'action.hover' }}
                            >
                              <Inventory2RoundedIcon fontSize="small" color="disabled" />
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {product.code ? (
                                  <Box
                                    component="span"
                                    sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                                  >
                                    {product.code}
                                  </Box>
                                ) : (
                                  '—'
                                )}
                                {product.brand ? ` · ${product.brand}` : ''}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Category — hidden on xs */}
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary">
                            {product.categoryName ?? '—'}
                          </Typography>
                        </TableCell>

                        {/* Governed price (web-price-engine-service enrichment, plan-catalog-007):
                            an amount only when the governance state is ACTIVE; otherwise the state
                            itself as a chip (NO_PRICE/PENDING/BLOCKED/EXPIRED — or UNAVAILABLE when
                            price-engine could not be reached: a visible degradation, not an error). */}
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {product.price != null ? (
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {formatPrice(product.price)}
                              </Typography>
                              {/* Provenance caption (design spec §8.11) — the scoped price's
                                  "where did this number come from"; absent in plain company
                                  scope with no group selected (byte-identical to today). */}
                              {(() => {
                                const caption = priceColumnCaption(t, product, scope, effectiveGroupCode, scopeNames);
                                return caption ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {caption}
                                  </Typography>
                                ) : null;
                              })()}
                            </Box>
                          ) : (
                            <Chip
                              size="small"
                              variant="outlined"
                              color={priceStatusColor(product.priceStatus)}
                              label={
                                product.priceStatus === 'UNAVAILABLE'
                                  ? t('products.priceUnavailable')
                                  : PRICE_STATUS_LABELS[product.priceStatus ?? 'NO_PRICE']
                                    ? t(PRICE_STATUS_LABELS[product.priceStatus ?? 'NO_PRICE'])
                                    : (product.priceStatus ?? '—')
                              }
                            />
                          )}
                        </TableCell>

                        {/* Status chips — 3 dimensions: authoring, buyer-state, publication */}
                        <TableCell>
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            {/*
                             * Authoring gate: cardStatus is the canonical storefront eligibility
                             * field. Backend default is ACTIVE (not DRAFT), so fall back to
                             * 'ACTIVE' when absent. Do NOT branch on lifecycleStatus.
                             */}
                            <Chip
                              size="small"
                              variant="outlined"
                              color={productCardStatusColor(product.cardStatus ?? 'ACTIVE')}
                              label={t(
                                PRODUCT_CARD_STATUS_LABELS[product.cardStatus ?? 'ACTIVE'] ??
                                  PRODUCT_CARD_STATUS_LABELS.ACTIVE,
                              )}
                            />
                            {/*
                             * Buyer-state chip: commercialAvailability is PRIMARY — the backend
                             * ignores stockStatus when commercialAvailability is set. Show it
                             * first; fall back to stockStatus only when absent.
                             */}
                            {product.commercialAvailability ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                color={commercialAvailabilityColor(product.commercialAvailability)}
                                label={
                                  COMMERCIAL_AVAILABILITY_LABELS[product.commercialAvailability]
                                    ? t(COMMERCIAL_AVAILABILITY_LABELS[product.commercialAvailability])
                                    : product.commercialAvailability
                                }
                              />
                            ) : (
                              /* Inventory-derived availability (plan-catalog-007): the chip carries
                                 the ledgered available quantity when tracked; UNAVAILABLE = the
                                 enrichment call failed (neutral dash, not an error). In a non-company
                                 scope (design spec §8.11) the tooltip names which point of sale the
                                 quantity describes. */
                              <Tooltip
                                title={
                                  scope.kind === 'company'
                                    ? ''
                                    : t('products.table.stockScopedTooltip', {
                                        scope:
                                          scope.kind === 'channel'
                                            ? scopeNames.channel(scope.code)
                                            : scopeNames.store(scope.code),
                                      })
                                }
                                disableHoverListener={scope.kind === 'company'}
                              >
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={stockStatusColor(product.stockStatus)}
                                  label={
                                    product.stockStatus === 'UNAVAILABLE'
                                      ? t('products.stockUnavailable')
                                      : STOCK_STATUS_LABELS[product.stockStatus ?? '']
                                        ? `${t(STOCK_STATUS_LABELS[product.stockStatus ?? ''])}${
                                            product.availableQty != null
                                              ? ` · ${formatQuantity(product.availableQty)}`
                                              : ''
                                          }`
                                        : (product.stockStatus ?? '—')
                                  }
                                />
                              </Tooltip>
                            )}
                            {/*
                             * Publication chip: warning only when product is ready to sell
                             * but not yet published. DRAFT/ARCHIVED → grey (expected state).
                             */}
                            <Chip
                              size="small"
                              variant="outlined"
                              color={
                                product.published
                                  ? 'success'
                                  : (product.cardStatus === 'ACTIVE' || product.cardStatus == null) &&
                                      product.commercialAvailability !== 'ARCHIVED'
                                    ? 'warning'
                                    : 'default'
                              }
                              label={
                                product.published
                                  ? t('products.publicationChip.published')
                                  : t('products.publicationChip.notPublished')
                              }
                            />
                          </Stack>
                        </TableCell>

                        {/* Row actions — stopPropagation so icon clicks don't open the detail drawer */}
                        <TableCell
                          align="right"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ cursor: 'default' }}
                        >
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title={priceActionTooltip}>
                              {/* span wrapper required for Tooltip on a disabled button */}
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={priceActionMode === 'none'}
                                  aria-label={t('products.rowActions.editPriceAria', { name: product.name })}
                                  onClick={() => setPriceTarget(product)}
                                >
                                  <LocalOfferRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={t('products.rowActions.stock')}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!canManage}
                                  aria-label={t('products.rowActions.stockAria', { name: product.name })}
                                  onClick={() => setStockTarget(summaryToProduct(product))}
                                >
                                  <WarehouseRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={canManage ? t('products.rowActions.edit') : t('products.rowActions.view')}
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={t('products.rowActions.editAria', { name: product.name })}
                                onClick={() => navigate(`/catalog/products/${product.id}`)}
                              >
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canManage && (
                              <Tooltip title={t('products.rowActions.delete')}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={t('products.rowActions.deleteAria', { name: product.name })}
                                  onClick={() => setDeleteTarget(product)}
                                >
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/*
               * CompactTablePagination — minimal-height footer row, directly below
               * the table (no separate pinned footer shell needed). Shows the
               * rows-per-page selector so merchants can adjust density.
               *
               * Reference: same visual format as ContentPagesPage > TablePagination.
               */}
              <CompactTablePagination
                count={total}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={(_e, next) => setPage(next)}
                onRowsPerPageChange={(e) =>
                  resetPageAnd(() => setSize(parseInt(e.target.value, 10)))
                }
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} ${t('common:pagination.of')} ${count}`
                }
                sx={{ borderTop: (t) => `1px solid ${t.palette.divider}` }}
              />
            </>
          )}
        </Box>
      </Stack>

      {/* ── Product detail drawer ─────────────────────────────────────────── */}
      {/*
       * Opens on row click — shows product photo, readiness checklist
       * (cardStatus · commercialAvailability · published · price), and
       * secondary attributes. Quick-edit actions in the footer navigate to
       * the full editor or open the quick-edit dialogs without closing the drawer.
       */}
      <ProductDetailDrawer
        product={drawerProduct}
        open={drawerProduct !== null}
        canManage={canManage}
        priceActionMode={priceActionMode}
        priceProvenanceCaption={
          drawerProduct ? priceColumnCaption(t, drawerProduct, scope, effectiveGroupCode, scopeNames) : undefined
        }
        onClose={() => setDrawerProduct(null)}
        onEdit={(id) => {
          setDrawerProduct(null);
          navigate(`/catalog/products/${id}`);
        }}
        onQuickPrice={(p) => setPriceTarget(p)}
        onQuickStock={(p) => setStockTarget(p)}
        onOpenPriceCard={(id) => {
          // §12.7 — the merchant came for the price via the row itself, so the card opens with
          // its Prices-360 drawer already showing (ignored pre-save, `?drawer=prices`).
          setDrawerProduct(null);
          navigate(`/catalog/products/${id}?drawer=prices`);
        }}
      />

      {/* ── Quick-edit dialogs ────────────────────────────────────────────── */}
      <QuickPriceDialog
        product={priceTarget ? summaryToProduct(priceTarget) : null}
        open={priceTarget !== null}
        context={priceTarget ? priceEditTarget?.context : undefined}
        storeCaption={priceTarget ? priceEditTarget?.storeCaption : undefined}
        readOnly={priceActionMode !== 'manage'}
        onClose={() => setPriceTarget(null)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />
      <QuickStockDialog
        product={stockTarget}
        open={stockTarget !== null}
        onClose={() => setStockTarget(null)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('products.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('products.delete.body', { name: deleteTarget?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {t('common:action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk stock update ─────────────────────────────────────────────── */}
      <BulkStockUpdateDialog
        open={bulkDialogOpen}
        selectedCount={selectedCount}
        totalCount={total}
        selectAllMode={selectAllMode}
        selectedIds={selectedProductIds}
        onClose={() => setBulkDialogOpen(false)}
        onResult={(severity, message) => {
          if (severity === 'success') {
            success(message);
            clearSelection();
          } else {
            notifyError(message);
          }
        }}
      />

      {/* ── Bulk repricing wizard (design spec §8.13) ───────────────────────── */}
      <PriceChangeBatchWizardDialog
        open={bulkRepriceOpen}
        initialProducts={bulkInitialProducts}
        initialTargetChannelCode={bulkTargetSeed.initialTargetChannelCode}
        initialTargetGroupCode={bulkTargetSeed.initialTargetGroupCode}
        initialTargetListCode={bulkTargetSeed.initialTargetListCode}
        initialTargetLabel={bulkTargetSeed.initialTargetLabel}
        targetInfoNote={bulkTargetSeed.targetInfoNote}
        pending={createBatchMutation.isPending || dryRunBatchMutation.isPending}
        onClose={() => setBulkRepriceOpen(false)}
        onSubmit={handleCreateRepriceBatch}
      />

      {/* ── Import flyout ─────────────────────────────────────────────────── */}
      <ImportFlyout open={importOpen} onClose={() => setImportOpen(false)} />

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </SectionScaffold>
  );
}
